import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BillingDetails,
  AnafCompanyDetails,
  AnafLookupApiResponse,
  SetupIntentApiResponse,
  SubscriptionDetails,
  SubscriptionPlan,
  SubscriptionPlanApiResponse,
  SubscriptionStatusApiResponse,
} from '../models/subscription.model';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private readonly apiUrl = `${environment.apiUrl}/subscription`;
  private readonly http = inject(HttpClient);
  private statusRequest?: Observable<SubscriptionDetails>;

  getPlans(): Observable<SubscriptionPlan[]> {
    return this.http.get<SubscriptionPlanApiResponse[]>(`${this.apiUrl}/plan`).pipe(
      map(plans => plans.map(plan => ({
        plan: plan.plan,
        amount: plan.amount,
        vatPercentage: plan.vat_percentage,
        vatAmount: plan.vat_amount,
        totalAmount: plan.total_amount,
        currency: plan.currency,
        interval: plan.interval,
        hasFreePeriod: plan.has_free_period,
        publishableKey: plan.publishable_key,
      }))),
    );
  }

  getStatus(forceRefresh = false): Observable<SubscriptionDetails> {
    if (forceRefresh || !this.statusRequest) {
      this.statusRequest = this.http.get<SubscriptionStatusApiResponse>(`${this.apiUrl}/status`).pipe(
        map(response => this.mapStatus(response)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.statusRequest;
  }

  clearStatus(): void {
    this.statusRequest = undefined;
  }

  createSetupIntent(): Observable<string> {
    return this.http.post<SetupIntentApiResponse>(`${this.apiUrl}/setup-intent`, {}).pipe(
      map(response => response.client_secret),
    );
  }

  createSubscription(setupIntentId: string, plan: SubscriptionPlan['plan']): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionStatusApiResponse>(this.apiUrl, {
      setup_intent_id: setupIntentId,
      plan,
    }).pipe(
      switchMap(() => this.getStatus(true)),
    );
  }

  updatePaymentMethod(setupIntentId: string): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionStatusApiResponse>(`${this.apiUrl}/payment-method`, {
      setup_intent_id: setupIntentId,
    }).pipe(
      switchMap(() => this.getStatus(true)),
    );
  }

  updateBillingDetails(details: BillingDetails): Observable<SubscriptionDetails> {
    return this.http.put<SubscriptionStatusApiResponse & { anaf_unverified?: boolean }>(`${this.apiUrl}/billing-details`, {
      legal_name: details.legalName,
      cui: details.cui,
      reg_com: details.regCom,
      address: details.address,
      city: details.city,
      county: details.county,
      postal_code: details.postalCode,
      vat_payer: details.vatPayer,
    }).pipe(switchMap(response => this.getStatus(true).pipe(
      map(status => ({ ...status, anafUnverified: !!response.anaf_unverified })),
    )));
  }

  lookupAnaf(cui: string): Observable<AnafCompanyDetails> {
    return this.http.post<AnafLookupApiResponse>(`${this.apiUrl}/billing-details/anaf`, { cui }).pipe(
      map(response => ({
        legalName: response.legal_name,
        regCom: response.reg_com,
        address: response.address,
        city: response.city,
        county: response.county,
        postalCode: response.postal_code,
        vatPayer: response.vat_payer,
      })),
    );
  }

  cancelSubscription(): Observable<SubscriptionDetails> {
    return this.http.delete<SubscriptionStatusApiResponse>(this.apiUrl).pipe(
      switchMap(() => this.getStatus(true)),
    );
  }

  resumeSubscription(): Observable<SubscriptionDetails> {
    return this.http.post<SubscriptionStatusApiResponse>(`${this.apiUrl}/resume`, {}).pipe(
      switchMap(() => this.getStatus(true)),
    );
  }

  private mapStatus(response: SubscriptionStatusApiResponse): SubscriptionDetails {
    return {
      status: response.status,
      plan: response.plan,
      accessAllowed: response.access_allowed,
      freePeriodEndsAt: response.free_period_ends_at,
      cancellationEndsAt: response.cancellation_ends_at,
      cardBrand: response.card_brand,
      cardLastFour: response.card_last_four,
      billingDetails: response.billing_details ? {
        legalName: response.billing_details.legal_name,
        cui: response.billing_details.cui,
        regCom: response.billing_details.reg_com,
        address: response.billing_details.address,
        city: response.billing_details.city,
        county: response.billing_details.county,
        postalCode: response.billing_details.postal_code,
        vatPayer: response.billing_details.vat_payer,
      } : null,
    };
  }
}
